import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Button, Alert } from '@mui/material';

/**
 * CommentInput Component
 * 
 * Allows users to add standalone comments to a change.
 * Validates comment length (1-1000 characters) and prevents empty/whitespace-only comments.
 * Shows character counter and error messages for invalid input.
 * Clears input after successful submission.
 * Auto-focuses the textarea when component mounts.
 * 
 * @param {Object} props
 * @param {Function} props.onSubmit - Callback function called with comment text when submitted
 * @param {Function} props.onCancel - Callback function called when Cancel button is clicked
 * @param {boolean} props.disabled - Whether the input is disabled (default: false)
 */
const CommentInput = ({ onSubmit, onCancel, disabled = false }) => {
  const [commentText, setCommentText] = useState('');
  const [error, setError] = useState('');
  const textareaRef = useRef(null);

  // Auto-focus the textarea when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleSubmit = async () => {
    const trimmed = commentText.trim();
    
    // Validate comment is not empty
    if (!trimmed) {
      setError('Comment cannot be empty');
      return;
    }
    
    // Validate comment length
    if (trimmed.length > 1000) {
      setError('Comment cannot exceed 1000 characters');
      return;
    }
    
    try {
      // Submit the comment; support both sync and async handlers.
      const result = await onSubmit(trimmed);

      // If a boolean is returned, treat `true` as success and `false` as failure.
      const isSuccess = typeof result === 'boolean' ? result : true;

      if (isSuccess) {
        // Clear input and error after successful submission
        setCommentText('');
        setError('');
      } else {
        setError('Failed to submit comment. Please try again.');
      }
    } catch (e) {
      // Preserve the user's comment text so it is not lost on failure.
      setError('Failed to submit comment. Please try again.');
    }
  };

  const handleChange = (e) => {
    setCommentText(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const isSubmitDisabled = disabled || !commentText.trim();

  return (
    <Box sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder="Add a comment..."
        value={commentText}
        onChange={handleChange}
        disabled={disabled}
        inputProps={{ maxLength: 1000 }}
        inputRef={textareaRef}
        helperText={`${commentText.length}/1000 characters`}
      />
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          Add Comment
        </Button>
      </Box>
    </Box>
  );
};

export default CommentInput;
